import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Article } from '@/types/article';
import { formatDate, formatViews } from '@/utils/articles';

interface HeroCarouselProps {
  items: Article[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % items.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;

  return (
    <div className="relative overflow-hidden rounded-lg border border-background-200">
      <div className="relative h-[300px] w-full md:h-[420px]">
        {items.map((item, idx) => (
          <Link
            key={item.id}
            to={`/detail/${item.id}`}
            className={`absolute inset-0 cursor-pointer transition-opacity duration-700 ${
              idx === active ? 'opacity-100' : 'pointer-events-none opacity-0'
            }`}
          >
            <img
              src={item.cover}
              alt={item.title}
              title={`${item.title} - 91黑料网`}
              className="h-full w-full object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-transparent"></div>
            <div className="absolute inset-x-0 bottom-0 p-5 md:p-8">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-accent-500 px-2.5 py-0.5 text-xs font-semibold text-background-50">
                  焦点爆料
                </span>
                <span className="rounded-full bg-white/15 px-2.5 py-0.5 text-xs text-white backdrop-blur">
                  {item.category}
                </span>
              </div>
              <h2 className="clamp-2 w-full text-xl font-black leading-snug text-white md:text-3xl">
                {item.title}
              </h2>
              <p className="clamp-2 mt-2 hidden max-w-3xl text-sm leading-relaxed text-white/80 md:block">
                {item.excerpt}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-white/70">
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {formatDate(item.publishTime)}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {formatViews(item.views)} 次浏览
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-newspaper-line"></i>
                  {item.source}
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="absolute bottom-4 right-5 flex items-center gap-2">
        {items.map((item, idx) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到第${idx + 1}张`}
            onClick={() => setActive(idx)}
            className={`h-2 cursor-pointer rounded-full transition-all ${
              idx === active ? 'w-6 bg-primary-500' : 'w-2 bg-white/60'
            }`}
          ></button>
        ))}
      </div>
    </div>
  );
}